import { useEffect } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';

import type { HomepageViewKey } from '@/shared/components/dropdowns/HomepageViewSelectDropdown/homepageViewRegistry';
import type {
    TaskDisplayedPropertyKey,
    TaskGroupBy,
    TaskSortBy,
    TaskSortDirection,
} from '@/shared/components/tasks/taskViewPreferences';
import { accountStorageKey } from '@/stores/auth/accountStorage';
import { SearchPreferences, UserPreference } from '@/types';
import type { FinancialValueDisplayPreference } from '@/types/financialValueDisplay';
import type { FinancialStatementDefaults } from '@/types/savedSeries';
import { deepMerge } from '@/utilities/deepMerge';

import { useUpdateUserPreferencesMutation, useUserPreferencesQuery } from '../queries';
import { useUserPreferenceStore } from '../store';

const LOCAL_STORAGE_KEY = 'user-preferences-cache';
const USER_PREFERENCES_QUERY_KEY = ['userPreferences'];

type PreferenceQueryData = {
    preferences?: UserPreferencesValue;
} & Record<string, unknown>;

export type UserPreferencesValue = {
    version?: number;
    home?: {
        dashboardId?: string | null;
        view?: HomepageViewKey;
        currentTab?: 'recentlyViewed' | 'favorites' | 'shared';
        sortOption?: 'updatedAt' | 'createdAt' | 'alphabetical';
        resource?: string;
    };
    quickActions?: {
        collapsed?: boolean;
    };
    toasts?: {
        expanded?: boolean;
    };
    search?: SearchPreferences;
    investments?: {
        rightSider?: {
            collapsed?: boolean;
        };
    };
    relations?: {
        dashboards?: {
            rightSider?: {
                collapsed?: boolean;
            };
        };
        lists?: {
            rightSider?: {
                collapsed?: boolean;
            };
        };
        tasks?: {
            displayedProperties?: TaskDisplayedPropertyKey[];
            groupBy?: TaskGroupBy;
            propertyOrder?: TaskDisplayedPropertyKey[];
            sortBy?: TaskSortBy;
            sortDirection?: TaskSortDirection;
        };
    };
    commandPalette?: {
        tabs?: Record<
            string,
            {
                hidden?: string[];
                order?: string[];
            }
        >;
    };
    entityDetail?: {
        tabs?: Record<
            string,
            {
                hidden?: string[];
                order?: string[];
            }
        >;
    };
    inbox?: {
        digest?: {
            customSchedule?: {
                timeOfDay?: string;
                timezone?: string;
                weekDays?: number[];
            };
            enabled?: boolean;
            frequency?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'semiAnnual' | 'annual' | 'custom';
        };
    };
    notifications?: {
        channels?: {
            email?: boolean;
            inApp?: boolean;
            push?: boolean;
            slack?: boolean;
        };
        slack?: {
            channelIds?: string[];
            connectedAccountId?: string;
        };
        workflows?: Record<
            string,
            {
                email?: boolean;
                inApp?: boolean;
                push?: boolean;
                slack?: boolean;
            }
        >;
    };
    leftSider?: {
        collapsed?: boolean;
        width?: number;
        sections?: Record<
            string,
            {
                collapsed?: boolean;
                order?: string[];
                hidden?: string[];
                groups?: Record<
                    string,
                    {
                        collapsed?: boolean;
                    }
                >;
            }
        >;
    };
    financialStatements?: {
        defaults?: FinancialStatementDefaults;
    };
    financialValues?: {
        display?: FinancialValueDisplayPreference | null;
    };
    financialOverview?: {
        charts?: Record<
            string,
            {
                keyEvents?: string[];
                metric?: string;
            }
        >;
        layouts?: Record<
            string,
            {
                hidden?: string[];
                order?: string[];
            }
        >;
        views?: Record<
            string,
            Record<string, unknown> & {
                financialValueDisplay?: FinancialValueDisplayPreference | null;
            }
        >;
    };
} & Record<string, unknown>;

const defaultPreferences = { version: 1 } as UserPreferencesValue;

type UpdatePreferencesMutateAsync = ReturnType<typeof useUpdateUserPreferencesMutation>['mutateAsync'];

type PersistenceContext = {
    mutateAsync: UpdatePreferencesMutateAsync;
    queryClient: QueryClient;
    setPreferences: (prefs: UserPreference) => void;
};

let persistenceContext: PersistenceContext | null = null;
let persistSequence = 0;
let inFlight = false;
let hasOptimisticUpdate = false;
let pendingUpdates: Partial<UserPreferencesValue> = {};
let rollbackPreferences: UserPreferencesValue | null = null;
let persistScheduled = false;

const readCachedPreferences = (): UserPreferencesValue | null => {
    const cacheKey = accountStorageKey(LOCAL_STORAGE_KEY);
    let cached: string | null;

    try {
        cached = localStorage.getItem(cacheKey);
    } catch {
        return null;
    }

    if (!cached) {
        return null;
    }

    try {
        return JSON.parse(cached);
    } catch {
        try {
            localStorage.removeItem(cacheKey);
        } catch {
            // Browser storage is only a cache; server preferences remain authoritative.
        }

        return null;
    }
};

const writeCachedPreferences = (preferences: UserPreferencesValue) => {
    try {
        localStorage.setItem(accountStorageKey(LOCAL_STORAGE_KEY), JSON.stringify(preferences));
    } catch {
        // Storage can be unavailable or over quota. Never let that block API persistence.
    }
};

const applyLocalPreferences = (context: PersistenceContext, preferences: UserPreferencesValue) => {
    context.setPreferences(preferences as unknown as UserPreference);
    writeCachedPreferences(preferences);
    context.queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, (current: PreferenceQueryData | undefined) => ({
        ...(current ?? {}),
        preferences,
    }));
};

const applyServerPreferences = (
    context: PersistenceContext,
    preferences: UserPreferencesValue,
    data: UserPreference,
) => {
    context.setPreferences(preferences as unknown as UserPreference);
    writeCachedPreferences(preferences);
    context.queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, data);
};

const persistPendingUpdates = () => {
    const context = persistenceContext;

    if (!context || inFlight) {
        return;
    }

    const sequence = persistSequence;
    const updates = pendingUpdates;

    inFlight = true;

    void context
        .mutateAsync(updates as Partial<UserPreference>)
        .then((data) => {
            inFlight = false;

            if (sequence !== persistSequence) {
                persistPendingUpdates();

                return;
            }

            hasOptimisticUpdate = false;
            pendingUpdates = {};
            rollbackPreferences = null;
            applyServerPreferences(context, data.preferences as unknown as UserPreferencesValue, data);
        })
        .catch(() => {
            inFlight = false;

            if (sequence !== persistSequence) {
                persistPendingUpdates();

                return;
            }

            const rollback = rollbackPreferences ?? defaultPreferences;

            hasOptimisticUpdate = false;
            pendingUpdates = {};
            rollbackPreferences = null;
            applyLocalPreferences(context, rollback);
        });
};

const schedulePersist = () => {
    if (persistScheduled) {
        return;
    }

    persistScheduled = true;
    queueMicrotask(() => {
        persistScheduled = false;
        persistPendingUpdates();
    });
};

export const useUserPreferences = () => {
    const { data } = useUserPreferencesQuery();
    const mutation = useUpdateUserPreferencesMutation();
    const queryClient = useQueryClient();

    const { selectedUserPreference, setPreferences } = useUserPreferenceStore();

    useEffect(() => {
        persistenceContext = {
            mutateAsync: mutation.mutateAsync,
            queryClient,
            setPreferences,
        };

        if (hasOptimisticUpdate && Object.keys(pendingUpdates).length > 0) {
            schedulePersist();
        }
    }, [mutation.mutateAsync, queryClient, setPreferences]);

    useEffect(() => {
        if (hasOptimisticUpdate) {
            return;
        }

        if (data?.preferences) {
            setPreferences(data.preferences);

            writeCachedPreferences(data.preferences);
        } else {
            const cached = readCachedPreferences();

            if (cached) {
                setPreferences(cached as unknown as UserPreference);
            }
        }
    }, [data, setPreferences]);

    const setPreference = (path: string, value: unknown) => {
        const keys = path.split('.');

        const nested = keys.reduceRight<unknown>(
            (acc, key) => ({ [key]: acc }),
            value,
        ) as Partial<UserPreferencesValue>;

        const previousPreferences =
            (useUserPreferenceStore.getState().selectedUserPreference as unknown as UserPreferencesValue | null) ??
            defaultPreferences;
        const optimisticPreferences = deepMerge(previousPreferences, nested);

        if (!hasOptimisticUpdate) {
            rollbackPreferences = previousPreferences;
            pendingUpdates = {};
        }

        pendingUpdates = deepMerge(pendingUpdates, nested);
        persistSequence += 1;
        hasOptimisticUpdate = true;
        schedulePersist();

        setPreferences(optimisticPreferences as unknown as UserPreference);
        writeCachedPreferences(optimisticPreferences);
        queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, (current: PreferenceQueryData | undefined) => ({
            ...(current ?? {}),
            preferences: optimisticPreferences,
        }));
    };

    return {
        preferences: selectedUserPreference as unknown as UserPreferencesValue | null,
        setPreference,
    };
};
